import {
  Button,
  Checkbox,
  Circle,
  CloseButton,
  Drawer,
  Field,
  Float,
  Input,
  Portal,
  Select,
  Text,
} from '@chakra-ui/react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdFilterAlt } from 'react-icons/md';
import { RiResetLeftFill } from 'react-icons/ri';

import SelectReferentials from '@/components/common/SelectReferentials';
import SelectSdgs from '@/components/common/SelectSdgs';
import SelectUnits from '@/components/common/SelectUnits';
import SelectUsers from '@/components/common/SelectUsers';
import { Tooltip } from '@/components/ui/tooltip';
import useMediaQuery from '@/hooks/use-media-query';
import { Route } from '@/routes/_layout/$slug/indicators/index.tsx';

import { category } from './IndicatorData';

const IndicatorFilterDrawer = ({ filter }: { filter: string }) => {
  const { slug } = useParams({ strict: false });
  const navigate = useNavigate({ from: Route.fullPath });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const defaultFilters = {
    commitment___pillar___name__ilike: '',
    commitment___name__ilike: '',
    name__ilike: '',
    category__in: [],
    description__ilike: '',
    general_data__exact: null,
    unit__in: [],
    sdgs___code__in: [],
    responsibles___id__in: [],
    referentials___global_referential_id__exact: null,
    referentials___global_referential_category_id__exact: null,
    referentials___global_referential_sub_category_id__exact: null,
  };
  const [filters, setFilters] = useState(() => ({ ...defaultFilters }));
  const { numberOfFilterActive } = useMemo(() => {
    const isActive = (value: any) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== null;
    };

    const activeCount = Object.values(filters).filter(isActive).length;

    return { numberOfFilterActive: activeCount };
  }, [filters]);
  const [resetTrigger, setResetTrigger] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (filter) {
      const parsedFilters: { [key: string]: any } = {};

      // Split the filter parameter (e.g., name=task&priority=high)
      const filterParams = new URLSearchParams(filter);

      // Populate the filters state with the values from the query parameter
      filterParams.forEach((value, key) => {
        if (key.endsWith('__in')) {
          parsedFilters[key] = value
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v !== '');
        } else if (key in filters) {
          const lower = value.toLowerCase();
          switch (lower) {
            case 'true':
              parsedFilters[key] = true;
              break;
            case 'false':
              parsedFilters[key] = false;
              break;
            default:
              if (key.endsWith('__ilike')) parsedFilters[key] = value.replace(/^%|%$/g, '');
              else parsedFilters[key] = value;
          }
        }
      });

      // Update the filters state
      setFilters((prev) => ({
        ...prev,
        ...parsedFilters,
      }));
    }
  }, [filter]);

  useEffect(() => {
    if (resetTrigger) {
      applyFilters();
      setResetTrigger(false);
    }
  }, [resetTrigger]);

  const handleFilterChange = (column: string, value: any) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
  };

  const applyFilters = () => {
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters)
        .filter(
          ([_, value]) =>
            value !== null &&
            value !== '' &&
            !(Array.isArray(value) && (value.length === 0 || (value.length === 1 && value[0] === '')))
        )
        .map(([key, value]) => {
          let stringValue = Array.isArray(value) ? value.join(',') : String(value);

          if (key.endsWith('__ilike')) {
            stringValue = `%${stringValue}%`;
          } else if (key.endsWith('__in')) {
            if (!stringValue.includes(',')) {
              stringValue = `${stringValue},`;
            }
          }

          return [key, stringValue];
        })
    );

    const encodedFilter = new URLSearchParams(cleanedFilters).toString();
    navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        filter: encodedFilter.replace(/\+/g, '%20'),
      }),
    });
    setIsDrawerOpen(false);
  };

  const resetFilters = () => {
    setFilters({ ...defaultFilters });
    setResetTrigger(true);
  };

  useEffect(() => {
    if (isDrawerOpen) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          applyFilters();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isDrawerOpen, filters]);
  const isTooltipHidden = useMediaQuery('(min-width: 1250px)');

  return (
    <Drawer.Root
      open={isDrawerOpen}
      onOpenChange={(e) => setIsDrawerOpen(e.open)}
      size="sm"
      placement="end"
      onEscapeKeyDown={() => applyFilters()}
    >
      <Drawer.Trigger as="div">
        <Tooltip
          showArrow
          content={t('ui.filter')}
          openDelay={100}
          closeDelay={100}
          contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
          disabled={isTooltipHidden}
        >
          <Button variant="outline" size="sm" {...(!isTooltipHidden && { p: '0' })}>
            <MdFilterAlt />
            {isTooltipHidden && <span>{t('ui.filter')}</span>}
            {numberOfFilterActive !== 0 && (
              <Float offsetY="1">
                <Circle size="4" bg="primary" color="white" fontSize={'xs'}>
                  {numberOfFilterActive}
                </Circle>
              </Float>
            )}
          </Button>
        </Tooltip>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop position={'fixed'} />
        <Drawer.Content
          rounded="md"
          position={'fixed'}
          margin={'{spacing.4}'}
          h={'calc(100% - {spacing.4} * 2)'}
          right={0}
          top={0}
        >
          <Drawer.CloseTrigger asChild mt="2" mr="4">
            <CloseButton size="sm" />
          </Drawer.CloseTrigger>
          <Drawer.Header>
            <Text textStyle={'xl'} fontWeight={'bold'}>
              {t('ui.filter')}
            </Text>
          </Drawer.Header>
          <Drawer.Body ref={contentRef}>
            {/* Pillar Name Filter */}
            <Field.Root>
              <Field.Label>{t('indicator.filter.pillarName.contains')}</Field.Label>
              <Input
                placeholder={t('ui.search')}
                value={filters.commitment___pillar___name__ilike}
                onChange={(e) => handleFilterChange('commitment___pillar___name__ilike', e.target.value)}
              />
            </Field.Root>

            {/* Commitment Name Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.commitmentName.contains')}</Field.Label>
              <Input
                placeholder={t('ui.search')}
                value={filters.commitment___name__ilike}
                onChange={(e) => handleFilterChange('commitment___name__ilike', e.target.value)}
              />
            </Field.Root>

            {/* General Data Filter */}
            <Field.Root mt={2}>
              <Field.Label />
              <Checkbox.Root
                checked={filters.general_data__exact == null ? 'indeterminate' : filters.general_data__exact}
                onCheckedChange={() => {
                  const newValue =
                    filters.general_data__exact == null ? true : filters.general_data__exact === true ? false : null;
                  handleFilterChange('general_data__exact', newValue);
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label>{t('indicator.filter.generalData')}</Checkbox.Label>
              </Checkbox.Root>
            </Field.Root>

            {/* Name Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.indicatorName.contains')}</Field.Label>
              <Input
                placeholder={t('ui.search')}
                value={filters.name__ilike}
                onChange={(e) => handleFilterChange('name__ilike', e.target.value)}
              />
            </Field.Root>

            {/* Category Filter */}
            <Field.Root mt={2}>
              <Select.Root
                collection={category}
                size="sm"
                value={filters.category__in}
                onValueChange={(e) => handleFilterChange('category__in', e.value)}
              >
                <Select.Label>{t('indicator.filter.category.select')}</Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('indicator.filter.category.select')} />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal container={contentRef}>
                  <Select.Positioner>
                    <Select.Content>
                      {category.items.map((el) => (
                        <Select.Item item={el} key={el.value}>
                          {el.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            </Field.Root>

            {/* Description Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.description.contains')}</Field.Label>
              <Input
                placeholder={t('ui.search')}
                value={filters.description__ilike}
                onChange={(e) => handleFilterChange('description__ilike', e.target.value)}
              />
            </Field.Root>

            {/* Users Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.responsibles.select')}</Field.Label>
              <SelectUsers
                slug={slug}
                value={filters.responsibles___id__in}
                onChange={(value) =>
                  handleFilterChange(
                    'responsibles___id__in',
                    value.map((user) => user.id.toString())
                  )
                }
              />
            </Field.Root>

            {/* SDGs Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.sdgs.select')}</Field.Label>
              <SelectSdgs
                value={filters.sdgs___code__in}
                onChange={(value) =>
                  handleFilterChange(
                    'sdgs___code__in',
                    value.map((sdg) => sdg.code)
                  )
                }
              />
            </Field.Root>

            {/* Referentials Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.referentials.select')}</Field.Label>
              <SelectReferentials
                value={[
                  {
                    id: null,
                    global_referential_id: filters.referentials___global_referential_id__exact,
                    global_referential_category_id: filters.referentials___global_referential_category_id__exact,
                    global_referential_sub_category_id:
                      filters.referentials___global_referential_sub_category_id__exact,
                  },
                ]}
                onChange={(value) => {
                  handleFilterChange('referentials___global_referential_id__exact', value[0].global_referential_id);
                  handleFilterChange(
                    'referentials___global_referential_category_id__exact',
                    value[0].global_referential_category_id
                  );
                  handleFilterChange(
                    'referentials___global_referential_sub_category_id__exact',
                    value[0].global_referential_sub_category_id
                  );
                }}
                disabledAdd={true}
                size={'sm'}
              />
            </Field.Root>

            {/* Unit Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.unit.contains')}</Field.Label>
              <SelectUnits slug={slug} value={filters.unit__in} onChange={(e) => handleFilterChange('unit__in', e)} />
            </Field.Root>
          </Drawer.Body>

          <Drawer.Footer>
            <Button variant="outline" mr={'auto'} onClick={resetFilters}>
              <RiResetLeftFill />
              {t('ui.reset')}
            </Button>
            <Button variant="outline" mr={3} onClick={() => setIsDrawerOpen(false)}>
              {t('ui.cancel')}
            </Button>
            <Button colorScheme="blue" onClick={applyFilters}>
              {t('ui.apply')}
            </Button>
          </Drawer.Footer>
        </Drawer.Content>
      </Portal>
    </Drawer.Root>
  );
};

export default IndicatorFilterDrawer;
